import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import { motion, AnimatePresence } from "framer-motion";
import { format, parseISO } from "date-fns";
import { Calendar, MapPin, Ticket, Loader2, Expand, X, Star, Info } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useScrollToTopWhenLoading } from "../hooks/useScrollToTopWhenLoading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Image Preview Modal Component
const ImagePreviewModal = ({ isOpen, onClose, src, alt }) => {
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95 backdrop-blur-md"
        onClick={onClose}
        data-testid="image-preview-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
          data-testid="close-preview-btn"
        >
          <X className="w-6 h-6" />
        </button>

        <motion.img
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          src={src}
          alt={alt}
          className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />
      </motion.div>
    </AnimatePresence>
  );
};

// Event Card Component - Redesigned with side-by-side layout
const EventCard = ({ event, index, onImagePreview }) => {
  const startDate = event.start_datetime ? parseISO(event.start_datetime) : new Date();
  const lowestPrice = getLowestPrice(event.ticket_types);
  const imageSrc = event.cover_image || "https://images.unsplash.com/photo-1508700929628-666bc8bd84ea?w=800";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      data-testid={`event-card-${event.id}`}
    >
      <Link to={`/events/${event.slug}`}>
        <Card className="card-empire overflow-hidden group cursor-pointer h-full hover:border-empire-gold/50 transition-all duration-300">
          {/* Mobile-first: Stack layout, Desktop: Side-by-side */}
          <div className="flex flex-col lg:flex-row">
            {/* Image Section - Full image display */}
            <div className="relative lg:w-1/2 xl:w-2/5 bg-neutral-900/50 flex items-center justify-center overflow-hidden">
              {/* Image Container - maintains full image without cropping */}
              <div className="relative w-full">
                <img
                  src={imageSrc}
                  alt={event.name}
                  className="w-full h-auto object-contain transition-transform duration-500 group-hover:scale-105"
                />
                
                {/* Gradient overlay for better text contrast */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                
                {/* Date Badge */}
                <div className="absolute top-4 left-4 bg-empire-gold text-black px-4 py-2 rounded-xl text-center shadow-lg">
                  <div className="text-2xl font-bold leading-tight">{format(startDate, "d")}</div>
                  <div className="text-sm font-semibold uppercase">{format(startDate, "MMM")}</div>
                </div>

                {/* Featured Badge */}
                {event.is_featured && (
                  <Badge className="absolute top-4 right-14 bg-empire-gold/20 text-empire-gold border border-empire-gold/50 backdrop-blur-sm">
                    <Star className="w-3 h-3 mr-1" fill="currentColor" />
                    Featured
                  </Badge>
                )}

                {/* Preview Button */}
                <button
                  onClick={(e) => onImagePreview(e, imageSrc, event.name)}
                  className="absolute top-4 right-4 p-2.5 bg-black/70 hover:bg-empire-gold hover:text-black backdrop-blur-sm rounded-lg text-white transition-all opacity-70 group-hover:opacity-100 z-10"
                  title="View full image"
                  data-testid={`preview-btn-${event.id}`}
                >
                  <Expand className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content Section */}
            <div className="lg:w-1/2 xl:w-3/5 p-5 sm:p-6 flex flex-col justify-between">
              {/* Top Content */}
              <div>
                {/* Event Type Badge */}
                <Badge variant="outline" className="mb-3 border-empire-gold/40 text-empire-gold text-xs uppercase tracking-wider">
                  {event.event_type}
                </Badge>
                
                {/* Event Title */}
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 group-hover:text-empire-gold transition-colors line-clamp-2">
                  {event.name}
                </h3>
                
                {/* Description */}
                <p className="text-neutral-400 text-sm mb-4 line-clamp-2 leading-relaxed">
                  {event.description}
                </p>

                {/* Key Info - Date & Location */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-empire-gold flex-shrink-0" />
                    <span className="text-neutral-300 font-medium">
                      {format(startDate, "EEEE, MMMM d • h:mm a")}
                    </span>
                  </div>
                  {event.venue_name && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-empire-gold flex-shrink-0" />
                      <span className="text-neutral-300 font-medium">{event.venue_name}</span>
                    </div>
                  )}
                </div>

                {/* Highlights Section - For important info like redeemable money */}
                {event.highlights && event.highlights.length > 0 && (
                  <div className="bg-empire-gold/10 border border-empire-gold/30 rounded-xl p-3 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Info className="w-4 h-4 text-empire-gold" />
                      <span className="text-empire-gold font-semibold text-sm">Highlights</span>
                    </div>
                    <ul className="space-y-1">
                      {event.highlights.map((highlight, idx) => (
                        <li key={idx} className="text-white text-sm flex items-start gap-2">
                          <span className="text-empire-gold mt-1">•</span>
                          <span className="font-medium">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Bottom Content - Price & CTA */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-neutral-800">
                {lowestPrice !== null ? (
                  <div className="flex items-baseline gap-2">
                    <span className="text-neutral-500 text-sm">From</span>
                    <span className="text-2xl sm:text-3xl font-bold text-empire-gold">
                      KES {lowestPrice.toLocaleString()}
                    </span>
                  </div>
                ) : (
                  <div className="text-neutral-500 text-sm">Price TBA</div>
                )}
                <Button className="btn-empire w-full sm:w-auto">
                  <Ticket className="w-4 h-4 mr-2" />
                  Get Tickets
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </Link>
    </motion.div>
  );
};

// Helper function to get lowest price from ticket types
const getLowestPrice = (ticketTypes) => {
  if (!ticketTypes || ticketTypes.length === 0) return null;
  return Math.min(...ticketTypes.map(t => t.price));
};

export const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewImage, setPreviewImage] = useState(null);

  useScrollToTopWhenLoading(loading);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events`);
      setEvents(res.data.events || res.data || []);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImagePreview = (e, imageSrc, imageAlt) => {
    e.preventDefault();
    e.stopPropagation();
    setPreviewImage({ src: imageSrc, alt: imageAlt });
  };

  return (
    <div className="container-empire" data-testid="events-page">
      <SEOHead page="events" />
      {/* Hero Section */}
      <section className="text-center mb-10 sm:mb-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <span className="font-cookie text-3xl sm:text-4xl md:text-5xl text-empire-gold block mb-2">
            Experience the magic
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-white mb-4">
            Upcoming Events
          </h1>
          <p className="text-neutral-400 text-base sm:text-lg max-w-xl mx-auto px-4">
            Showcases, workshops, and social dance nights. Join our community events.
          </p>
        </motion.div>
      </section>

      {/* Events List */}
      <section className="px-2 sm:px-0">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-20">
            <Ticket className="w-16 h-16 text-neutral-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No upcoming events</h3>
            <p className="text-neutral-500">Check back soon for new events!</p>
          </div>
        ) : (
          <div className="space-y-6 sm:space-y-8 max-w-5xl mx-auto">
            {events.map((event, index) => (
              <EventCard
                key={event.id}
                event={event}
                index={index}
                onImagePreview={handleImagePreview}
              />
            ))}
          </div>
        )}
      </section>

      {/* Image Preview Modal */}
      <ImagePreviewModal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        src={previewImage?.src}
        alt={previewImage?.alt}
      />
    </div>
  );
};
